import { AfterViewInit, Component, ElementRef, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconDirective, NzIconModule, NzIconService } from 'ng-zorro-antd/icon';
import { NzInputDirective, NzInputGroupComponent, NzInputGroupWhitSuffixOrPrefixDirective } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalRef, NzModalService, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzTableCellDirective, NzTableComponent, NzTableModule, NzTbodyComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective, NzTrExpandDirective } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzProgressComponent } from 'ng-zorro-antd/progress';
import { ProductionOrderProcessDto } from 'src/app/shared/dto/production-order-process.dto';
import { DatePipe, NgClass } from '@angular/common';
import _ from 'lodash';
import { TITLE_TABLE_SIZE } from 'src/app/core/constants/style';
import { IconDefinition } from '@ant-design/icons-angular';
import { FundViewOutline } from '@ant-design/icons-angular/icons';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { ProductionOrderDto } from 'src/app/shared/dto/production-order.dto';
import { ConfigurationService } from 'src/app/private/services/configuration.service';

const icons: IconDefinition[] = [
    FundViewOutline
];

@Component({
    selector: 'app-modal-production-processess-selector',
    standalone: true,
    imports: [
        NzModalFooterDirective,
        NzTagModule,
        NzTableModule,
        NzIconModule,
        FormsModule,
        ReactiveFormsModule,
        NzEmptyModule,
        NzTableComponent,
        NzTheadComponent,
        NzTrDirective,
        NzTableCellDirective,
        NzModalTitleDirective,
        NzThMeasureDirective,
        NzTbodyComponent,
        NzMessageModule,
        NzInputDirective,
        NzInputGroupComponent,
        NzInputGroupWhitSuffixOrPrefixDirective,
        NzIconDirective,
        NgClass,
        DatePipe,
        NzProgressComponent,
        NzToolTipModule,
        LoaderComponent
    ],
    templateUrl: './modal-production-processess-selector.component.html',
    styleUrl: './modal-production-processess-selector.component.scss'
})
export class ModalProductionProcessessSelectorComponent extends DefaultComponent implements OnInit, AfterViewInit{
	@Output() emitService = new EventEmitter();
	@ViewChild('modalContent') private readonly _modalContent!: ElementRef;

	productionOrderProcesses: ProductionOrderProcessDto[] = [];
	isLoading: boolean = true;
	tableHeight!: number;
	data = inject(NZ_MODAL_DATA);
	searchProductionOrderProcesses: string = '';
    idColumnValueToShow: string = 'id';

	private readonly router = inject(Router);
	private readonly modalService = inject(NzModalService);
	private readonly modalRef = inject(NzModalRef);
	private readonly message = inject(NzMessageService);
	private readonly iconService = inject(NzIconService);
    private readonly configurationService = inject(ConfigurationService);

    constructor() {
        super('productionOrders', '');        
    }

	async ngOnInit() {
	    this.iconService.addIcon(...icons);
	    this.productionOrderProcesses = this.data?.productionOrderProcesses;
        this.idColumnValueToShow = (await this.configurationService.getConfigurationByKey('order.idColumnValue')).value.toString() ?? 'id';
	    this.isLoading = false;
	}

	async ngAfterViewInit(): Promise<void> {
	    setTimeout(() => {
	        this.tableHeight =
				(this._modalContent.nativeElement as HTMLImageElement)
				    .clientHeight - TITLE_TABLE_SIZE;
	    });
	}

	calculateProductionProcessProgress(process: ProductionOrderProcessDto): number {

		if (!process.finalProducts) return 0;

		const quantityToProduce = process.finalProducts.reduce((total, finalProduct) => {
			return total + (finalProduct.quantityToProduce || 0);
		}, 0);

		const quantityProduced = process.finalProducts.reduce((total, finalProduct) => {
			return total + (finalProduct.quantityProduced || 0);
		}, 0);

	    return (quantityProduced === 0 || quantityToProduce === 0) ? 0 : Math.round((quantityProduced / quantityToProduce) * 100);
	}

	async navigateToProductionOrder(orderId: number, processId:number): Promise<void> {
	    if (await this.showOpenInNewTabDialog()) {
	        this.router.navigate([]).then(() => window.open(`/production-orders/${orderId}/process/${processId}`, '_blank'));
	    } else {
	        this.router.navigate([`/production-orders/${orderId}/process/${processId}`]);
	    }
	}

	async showOpenInNewTabDialog(): Promise<boolean> {
	    return new Promise((resolve) => {
	        this.modalService.confirm({
	            nzTitle: '<i>¿Desea abrir el proceso de la orden de producción seleccionada en otra pestaña del navegador?</i>',
	            nzClosable: false,
	            nzOkText: 'Nueva pestaña',
	            nzOnOk: () => { this.modalRef.close(); resolve(true); },
	            nzCancelText: 'Permanecer en esta',
	            nzOnCancel: () => { this.modalRef.close(); resolve(false); }
	        });
	    });
	}

	onSearch(event: string) {
	    try {
	        this.productionOrderProcesses = this.data.productionOrderProcesses.filter((productionOrderProcess: ProductionOrderProcessDto) => (productionOrderProcess.productionOrder?.description?.toLowerCase().includes(event.toLowerCase()) || productionOrderProcess.productionOrder?.id?.toString() === (event.toLowerCase())));
	    } catch (error) {
	        this.message.error(`Error al filtrar las órdenes de producción`);
	    }
	}

	async clearFilter(): Promise<void> {
	    this.isLoading = true;
	    this.searchProductionOrderProcesses = '';
	    this.productionOrderProcesses = _.cloneDeep(this.data.productionOrderProcesses);
	    this.isLoading = false;
	}

	handleCancel() {
	    this.modalRef.close();
	}

    showWithProductionOrdersPermissions(btnName: string): boolean {
        const permissionMainKey: string = 'productionOrders';

        return this.permissionService.canRead(permissionMainKey, `${btnName}`);
    }

    getIdColumnValue(process: any): string {
        return process?.finalProducts?.[0]?.finalProduct.articleBatch?.batchNumber || '';
    }
}
